import type { UpdateContactInput } from 'app/generated/gql/graphql'
import { CheckCircleIcon } from '@heroicons/react/20/solid'
import { ColumnDef } from '@tanstack/react-table'
import { Check, DollarCircle, XmarkCircle } from 'iconoir-react'
import { compact, kebabCase, uniqBy } from 'lodash'
import moment from 'moment'
import { AdPlatformMappingCell } from 'app/components/contacts/AdPlatformMappingCell'
import { ChannelMappingCell } from 'app/components/contacts/ChannelMappingCell'
import { ContactPipelinesCell } from 'app/components/contacts/ContactPipelinesCell'
import { ContactTagsCell } from 'app/components/contacts/ContactTagsCell'
import { LazyAdCampaign } from 'app/components/contacts/LazyAdCampaign'
import { LazyLastCommCell } from 'app/components/contacts/LazyLastCommCell'
import { SmsConsentCell } from 'app/components/contacts/SmsConsentCell'
import { LazyTaskStatusCell } from 'app/components/contacts/LazyTaskStatusCell'
import { LeadStatusCell } from 'app/components/contacts/LeadStatusCell'
import { DateComponent } from 'app/components/DateComponent'
import { LazyWorkspaceCell } from 'app/components/LazyWorkspaceCell'
import PhoneNumber from 'app/components/PhoneNumber'
import { ContactCell } from 'app/components/shared/ContactCell'
import { LazyLocationCard } from 'app/components/shared/LazyLocationCard'
import { AnchorCell } from 'app/components/table/AnchorCell'
import { CampaignComboOption } from 'app/components/table/filters/CampaignComboOption'
import { InlineEditCell } from 'app/components/table/InlineEditCell'
import { StaffAssignCell } from 'app/components/table/StaffAssignCell'
import TooltipWrapper from 'app/components/table/TooltipWrapper'
import { UserAssignCell } from 'app/components/table/UserAssignCell'
import {
  DateRangeOperator,
  FilterValueType,
  FilterableColumn,
  PredefinedFilter,
  addDefaultFilterProperties,
} from 'app/filter.utils'
import { HYGIENE_DUE_SOON_TAB, HYGIENE_OVERDUE_TAB, isHygieneTab } from 'app/lib/hygieneTabs'
import type { AdCampaign } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { Permission } from 'app/types/entities'
import { notifySuccess } from 'app/services/notify.client'
import { graphql } from 'app/generated/gql'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import { staffMembersFetcher } from 'app/lib/fetchers/staffMembersFetcher'
import { pipelinesFilterFetcher, pipelineStagesFilterFetcher } from 'app/lib/fetchers/pipelineFilterFetchers'
import { tagFilterOptionsFetcher } from 'app/lib/fetchers/tagFilterOptionsFetcher'

const UPDATE_CONTACT_MUTATION = graphql(`
  mutation UpdateContactTable($id: ID!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      id
    }
  }
`)
import type { SearchResponse } from 'app/services/search'
import { bg } from 'lib/background'
import { comActivityTypes } from 'lib/comActivityTypes'
import type { ListCopy } from 'lib/listCopy'
import { Jsonify } from 'lib/toJSON'
import { OptionType } from 'types/OptionType'
import { MONEY_ICON_FILL } from 'app/lib/iconColors'

export type FilterableColumnProps = {
  addMarketingFilters: boolean
  isOrgView: boolean
  preAppliedFilters?: PredefinedFilter
  isNeedsActionDateEnabled?: boolean
  type: string
  isSMC: boolean
  isSuper: boolean
  slug?: string
  isWokspaceUser?: boolean
  staffV2Enabled?: boolean
  checkPermission: (permission: Permission | Permission[]) => boolean
  hygieneEnabled?: boolean
  /**
   * Whether this user has the Action Center, and with it the three system
   * pipelines. Off means Hygiene Recall and Unscheduled treatment are left out
   * of the two pipeline filters: their boards live behind `FeatureRoute`, so
   * filtering the table by one leads somewhere the user cannot go.
   */
  actionCenterEnabled?: boolean
  /** Pipeline-or-list wording for the two membership filters; from `useListCopy` at the call site. */
  copy: ListCopy
}

const lifecycleStages: ContactJson[`lifecycleStage`][] = [`Lead`, `Qualified Lead`, `New Patient Scheduled`, `Patient`]

const Age = (props) => (
  <>{props.row.original.birthDate && (moment().diff(props.row.original.birthDate, `years`) || `0`)}</>
)

// Update from TRK-1623
export const defaultColumns = (
  /** Pipeline-or-list wording for the membership column; from `useListCopy` at the call site. */
  copy: ListCopy,
  contactHandler: (contactId: string, patientIds?: string[], index?: number) => void,
  updateContact: (contact: UpdateContactInput & { id?: string }) => void,
  closeDrawerHandler: () => void,
  checkPermission: (permission: Permission | Permission[]) => boolean,
  addMarketingFilters: boolean,
  isSMC: boolean,
  isSuper: boolean,
  enableLastCommDate?: boolean,
  enableTasks?: boolean,
  isNeedsActionDateEnabled?: boolean,
  slug?: string,
  staffV2Enabled?: boolean,
  baseUrl?: string,
  staffDashboardV2?: boolean,
  extraColumns: ColumnDef<ContactJson, any>[] = [],
): ColumnDef<ContactJson, any>[] => {
  let addMarketing = false
  let addUtm = false
  const marketingColumns: ColumnDef<ContactJson, any>[] = [
    {
      id: `platform`,
      header: `Ad Platform`,
      accessorKey: `platform`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `platform.keyword`,
        ControlledComponent: (row) => <AdPlatformMappingCell contact={row} />,
      },
    },
    {
      id: `Ad Campaign`,
      header: `Ad Campaign`,
      accessorKey: `trackableCampaignId`,
      enableSorting: false,
      cell: (props) => <LazyAdCampaign campaignId={props.getValue()} showUnknown />,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
      },
    },
  ]
  const utmColumns: ColumnDef<ContactJson, any>[] = [
    {
      id: `utmSource`,
      header: `Source`,
      accessorKey: `utmSource`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `utmSource.keyword`,
        tooltipClassName: `lowercase`,
        ControlledComponent: (row) =>
          isSMC ? (
            <TooltipWrapper value={row?.utmSource} />
          ) : (
            <InlineEditCell
              value={row?.utmSource}
              defaultEmptyValue="direct"
              className="text-body-xs text-default"
              handleSubmit={(value) => {
                if (row?.utmSource === value) return
                updateContact({ id: row?.id, utmSource: value })
              }}
              triggerEditSideEffect={closeDrawerHandler}
            />
          ),
      },
    },
    {
      id: `utmMedium`,
      header: `Medium`,
      accessorKey: `utmMedium`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `utmMedium.keyword`,
        tooltipClassName: `lowercase`,
        ControlledComponent: (row) =>
          isSMC ? (
            <TooltipWrapper value={row?.utmMedium} />
          ) : (
            <InlineEditCell
              value={row?.utmMedium}
              defaultEmptyValue="(not set)"
              className="text-body-xs text-default"
              handleSubmit={(value) => {
                if (row?.utmMedium === value) return
                updateContact({ id: row?.id, utmMedium: value })
              }}
              triggerEditSideEffect={closeDrawerHandler}
            />
          ),
      },
    },
    {
      id: `utmCampaign`,
      header: `Campaign`,
      accessorKey: `utmCampaign`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `utmCampaign.keyword`,
        tooltipClassName: `lowercase`,
        ControlledComponent: (row) =>
          isSMC ? (
            <TooltipWrapper value={row?.utmCampaign} />
          ) : (
            <InlineEditCell
              value={row?.utmCampaign}
              defaultEmptyValue="(not set)"
              className="text-body-xs text-default"
              handleSubmit={(value) => {
                if (row?.utmCampaign === value) return
                updateContact({ id: row?.id, utmCampaign: value })
              }}
              triggerEditSideEffect={closeDrawerHandler}
            />
          ),
      },
    },
    {
      id: `content`,
      accessorKey: `utmContent`,
      header: `Content`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `utmContent.keyword`,
        tooltipClassName: `lowercase`,
      },
    },
    {
      id: `utmKeywords`,
      accessorKey: `utmKeywords`,
      header: `Keyword`,
      meta: {
        defaultVisible: false,
        group: `Marketing`,
        sortKey: `utmKeywords.keyword`,
        tooltipClassName: `lowercase`,
        missing: `_last`,
      },
    },
    {
      id: `reportedAttribution`,
      header: `Reported Attribution`,
      accessorKey: `reportedAttribution`,
      meta: {
        getCellStyle: () => `lowercase`,
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `reportedAttribution.keyword`,
      },
    },
  ]
  let columnsWithPermissions: ColumnDef<ContactJson, any>[] = []
  if (staffV2Enabled) {
    addMarketing = isSuper || checkPermission(`org:marketing:advanced-filters`)
    addUtm = !isSMC && (isSuper || checkPermission(`org:contacts:see-utm`))
    columnsWithPermissions = compact([...(addMarketing ? marketingColumns : []), ...(addUtm ? utmColumns : [])])
  } else {
    columnsWithPermissions = compact(addMarketingFilters ? [...marketingColumns, ...utmColumns] : [])
  }
  return [
    {
      id: `name`,
      accessorKey: `name`,
      header: `Name`,
      enableResizing: true,
      meta: {
        group: `Contact Information`,
        defaultVisible: true,
        pinned: true,
        sortKey: `name.plain`,
        missing: `_last`,
        ControlledComponent: (row, col, index) => (
          <AnchorCell
            href={`${baseUrl || ``}&drawer=${row?.id!}`}
            clickHandler={() => contactHandler(row?.id!, row?.patientIds, index as number)}
          >
            <ContactCell contact={row as ContactJson} />
          </AnchorCell>
        ),
      },
      enableHiding: false,
      minSize: 160,
    },
    ...extraColumns,
    {
      id: `birthDate`,
      accessorKey: `birthDate`,
      header: `Age`,
      cell: Age,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        sortKey: `birthDate`,
      },
    },
    {
      id: `homePhone`,
      accessorKey: `homePhone`,
      header: `Home Phone`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        pinned: false,
        sortKey: `homePhone.keyword`,
      },
      cell: (props) => <PhoneNumber number={props.row.original.homePhone} />,
    },
    {
      id: `cellPhone`,
      accessorKey: `cellPhone`,
      header: `Cell Phone`,
      cell: (props) => <PhoneNumber number={props.row.original.cellPhone} />,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        pinned: false,
        sortKey: `cellPhone.keyword`,
      },
    },
    {
      id: `workPhone`,
      accessorKey: `workPhone`,
      header: `Work Phone`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        pinned: false,
        sortKey: `workPhone.keyword`,
      },
      cell: (props) => <PhoneNumber number={props.row.original.workPhone} />,
    },
    {
      id: `otherPhone`,
      accessorKey: `otherPhone`,
      header: `Other Phone`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        sortKey: `otherPhone.keyword`,
      },
    },
    {
      id: `preferredName`,
      accessorKey: `preferredName`,
      header: `Preferred Name`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        sortKey: `preferredName.keyword`,
      },
    },
    {
      id: `preferredContact`,
      accessorKey: `preferredContact`,
      header: `Preferred Contact`,
      meta: {
        sortKey: `preferredContact.keyword`,
        group: `Contact Information`,
        defaultVisible: false,
        pinned: false,
      },
    },
    {
      id: `preferredCommunicationMethod`,
      accessorKey: `preferredCommunicationMethod`,
      header: `Preferred Communication Method`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        pinned: false,
        sortKey: `preferredCommunicationMethod.keyword`,
      },
    },
    {
      id: `email`,
      accessorKey: `email`,
      header: `Email`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        sortKey: `email.keyword`,
      },
    },
    {
      id: `workspaceId`,
      accessorKey: `workspaceId`,
      header: `Workspace`,
      cell: (props) => <LazyWorkspaceCell workspaceId={props.row.original.workspaceId} />,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
      },
      enableSorting: false,
    },
    {
      id: `locationId`,
      accessorKey: `locationId`,
      header: `Location`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        sortKey: `locationId.keyword`,
        ControlledComponent: (row) => <LazyLocationCard locationId={row?.locationId} />,
      },
      enableSorting: true,
    },
    {
      id: `tags`,
      accessorFn: (row) => row.tags,
      header: `Tags`,
      minSize: 200,
      meta: {
        group: `Contact Information`,
        defaultVisible: true,
        ControlledComponent: (row) => <ContactTagsCell contact={row as ContactJson} />,
      },
      enableSorting: false,
    },
    ...(enableLastCommDate
      ? [
          {
            id: `lastCommDate`,
            accessorKey: `lastCommDate`,
            header: `Last Communication`,
            minSize: 220,
            meta: {
              group: `Dates`,
              defaultVisible: slug === `active-leads` || isHygieneTab(slug),
              sortKey: `lastCommDate`,
              ControlledComponent: (row) => (
                <LazyLastCommCell
                  contactId={row?.id}
                  lastCommDate={row?.lastCommDate}
                  lastCommActivityType={row?.lastCommActivityType}
                  lastCommActivitySubType={row?.lastCommActivitySubType}
                />
              ),
            },
          },
        ]
      : []),
    {
      id: `pipelines`,
      accessorKey: `pipelines`,
      header: copy.NounPlural,
      minSize: 240,
      enableSorting: false,
      meta: {
        group: `Contact Information`,
        defaultVisible: slug === `active-leads`,
        ControlledComponent: (row) => <ContactPipelinesCell contact={row as ContactJson} />,
      },
    },
    {
      id: `scheduledProduction`,
      accessorKey: `scheduledProduction`,
      cell: (info) => `$${info.getValue()?.toLocaleString(`en-US`) ?? 0}`,
      header: `Scheduled Production`,
      meta: {
        group: `Financial Information`,
        defaultVisible: false,
        sortKey: `scheduledProduction`,
      },
    },
    {
      id: `productionLtv`,
      accessorKey: `productionLtv`,
      accessorFn: (row) => `$${row.productionLtv?.toLocaleString(`en-US`) ?? 0}`,
      cell: (info) => info.getValue(),
      header: `Net Production`,
      meta: {
        group: `Financial Information`,
        defaultVisible: false,
        sortKey: `productionLtv`,
      },
    },
    {
      id: `fvv`,
      accessorKey: `fvv`,
      accessorFn: (row) => `$${row.fvv?.toLocaleString(`en-US`) ?? 0}`,
      cell: (info) => info.getValue(),
      header: `First Visit Value`,
      meta: {
        group: `Financial Information`,
        defaultVisible: false,
        sortKey: `fvv`,
      },
    },
    {
      id: `collectedLtv`,
      accessorKey: `collectedLtv`,
      accessorFn: (row) => `$${row.collectedLtv?.toLocaleString(`en-US`) ?? 0}`,
      cell: (info) => info.getValue(),
      header: `Collected`,
      meta: {
        group: `Financial Information`,
        defaultVisible: false,
        sortKey: `collectedLtv`,
      },
    },
    {
      id: `lifecycleStage`,
      accessorKey: `lifecycleStage`,
      header: `Lifecycle Stage`,
      meta: {
        group: `Lead and Conversion`,
        defaultVisible: false,
        pinned: false,
        sortKey: `lifecycleStage.keyword`,
      },
    },
    {
      id: `leadOwner`,
      header: `Contact Owner`,
      accessorKey: `leadOwner`,
      minSize: 140,
      meta: {
        group: `Lead and Conversion`,
        defaultVisible: true,
        pinned: false,
        sortKey: `leadOwner.name.keyword`,
        ControlledComponent: (row) =>
          staffDashboardV2 ? (
            <UserAssignCell
              user={row?.leadOwner as any}
              contactId={row?.id}
              locationId={row?.locationId}
              workspaceId={row?.workspaceId}
              firstNameInitial
              onChange={(val?: OptionType) => {
                bg(async () => {
                  if (!val?.value) return
                  if (!row?.id) return
                  await gqlMutation(UPDATE_CONTACT_MUTATION, { id: row.id, input: { leadOwner: val.value } })
                  notifySuccess(`Contact Owner updated successfully`)
                })
              }}
            />
          ) : (
            <StaffAssignCell
              {...row}
              firstNameInitial
              onChange={(val?: OptionType) => {
                bg(async () => {
                  if (!val?.value) return
                  if (!row?.id) return
                  await gqlMutation(UPDATE_CONTACT_MUTATION, { id: row.id, input: { leadOwner: val.value } })
                  notifySuccess(`Contact Owner updated successfully`)
                })
              }}
            />
          ),
      },
    },
    {
      id: `leadStatus`,
      accessorKey: `leadStatus`,
      header: `Lead Status`,
      minSize: 130,
      meta: {
        group: `Lead and Conversion`,
        defaultVisible: true,
        sortKey: `leadStatus.keyword`,
        ControlledComponent: (row) => (
          <LeadStatusCell
            contact={row}
            editable={
              staffV2Enabled ? checkPermission([`org:contacts:edit-lead-status`, `contacts:lead-status`]) : true
            }
          />
        ),
      },
    },
    ...(enableTasks
      ? [
          {
            id: `taskStatus`,
            header: `Tasks`,
            minSize: 160,
            meta: {
              group: `Lead and Conversion`,
              defaultVisible: slug === `active-leads` || isHygieneTab(slug),
              ControlledComponent: (row) => (
                <LazyTaskStatusCell contact={row} onOpenContact={() => row.id && contactHandler(row.id)} />
              ),
            },
            enableSorting: false,
          },
        ]
      : []),
    ...(isSuper
      ? [
          {
            id: `neededAction`,
            accessorFn: (row) => (row.neededAction ? `Yes` : `No`),
            header: `Needed Action`,
            minSize: 140,
            meta: {
              group: `Lead and Conversion`,
              defaultVisible: !isHygieneTab(slug),
              sortKey: `neededAction`,
            },
          },
        ]
      : []),
    {
      id: `closedLostReason`,
      accessorKey: `closedLostReason`,
      header: `Closed Lost Reason`,
      meta: {
        group: `Lead and Conversion`,
        defaultVisible: false,
        sortKey: `closedLostReason.keyword`,
      },
    },
    {
      id: `channel`,
      accessorKey: `channel`,
      header: `Channel`,
      minSize: 100,
      meta: {
        group: `Lead and Conversion`,
        defaultVisible: !isHygieneTab(slug),
        sortKey: `channel.keyword`,
        ControlledComponent: (row) => <ChannelMappingCell contact={row} />,
      },
    },
    {
      id: `firstContactReason`,
      accessorKey: `firstContactReason`,
      header: `First Contacted Reason`,
      meta: {
        group: `Lead and Conversion`,
        defaultVisible: false,
        sortKey: `firstContactReason.keyword`,
      },
    },
    {
      id: `isQualified`,
      accessorKey: `isQualified`,
      header: `Qualification Status`,
      cell: (props) => (props.getValue() ? `Qualified` : props.getValue() === false ? `Not Qualified` : ``),
      meta: {
        group: `Lead and Conversion`,
        defaultVisible: false,
        sortKey: `isQualified`,
      },
    },
    {
      id: `appointmentStatus`,
      accessorKey: `appointmentStatus`,
      header: `Appointment Status`,
      meta: {
        group: `Lead and Conversion`,
        defaultVisible: false,
        sortKey: `appointmentStatus.keyword`,
      },
    },
    {
      id: `smsMarketingConsent`,
      accessorKey: `smsMarketingConsent`,
      header: `SMS Consent`,
      cell: (props) => <SmsConsentCell value={props.getValue()} />,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `smsMarketingConsent`,
      },
    },
    {
      id: `firstActivityType`,
      header: `First Activity Type`,
      accessorKey: `firstActivityType`,
      meta: {
        group: `Lead and Conversion`,
        defaultVisible: false,
        sortKey: `firstActivityType.keyword`,
      },
    },
    {
      id: `patientName`,
      accessorKey: `patientName`,
      header: `Patient Name`,
      meta: {
        group: `Patient Information`,
        defaultVisible: false,
        sortKey: `patientName.keyword`,
      },
    },
    {
      id: `patientStatus`,
      accessorFn: (props) => kebabCase(props.patientStatus),
      header: `Patient Status`,
      meta: {
        group: `Patient Information`,
        defaultVisible: false,
        sortKey: `patientStatus.keyword`,
      },
    },
    {
      id: `patientCreatedDate`,
      accessorKey: `patientCreatedDate`,
      header: `Patient Created Date`,
      minSize: 120,
      cell: (props) => DateComponent({ value: props.row.original.patientCreatedDate, dateOnly: true }),
      meta: {
        group: `Patient Information`,
        defaultVisible: false,
        sortKey: `patientCreatedDate`,
      },
    },
    {
      id: `patientIds`,
      accessorKey: `patientIds`,
      header: `Patient Linked`,
      meta: {
        group: `Patient Information`,
        defaultVisible: false,
        sortKey: `patientIds.keyword`,
      },
      cell: (props) => (
        <span>
          {props.getValue() ? (
            <CheckCircleIcon className="h-5 w-5 fill-current text-blue-400" />
          ) : (
            <XmarkCircle className="h-5 w-5 text-red-400" />
          )}
        </span>
      ),
    },
    {
      id: `lastHygieneDate`,
      accessorKey: `lastHygieneDate`,
      header: `Last Hygiene Date`,
      minSize: 120,
      cell: (props) => DateComponent({ value: props.row.original.lastHygieneDate, dateOnly: true }),
      meta: {
        group: `Patient Information`,
        defaultVisible: isHygieneTab(slug),
        sortKey: `lastHygieneDate`,
      },
    },
    {
      id: `hygieneDueDate`,
      accessorKey: `hygieneDueDate`,
      header: `Hygiene Due Date`,
      minSize: 120,
      cell: (props) => DateComponent({ value: props.row.original.hygieneDueDate, dateOnly: true }),
      meta: {
        group: `Patient Information`,
        defaultVisible: false,
        sortKey: `hygieneDueDate`,
      },
    },
    {
      id: `daysPastDue`,
      accessorFn: (row) => {
        if (!row.hygieneDueDate) return undefined
        const today = moment()
        if (today.isBefore(moment(row.hygieneDueDate))) return undefined
        return today.diff(moment(row.hygieneDueDate), `days`)
      },
      header: `Days Past Due`,
      minSize: 120,
      cell: (props) => {
        const days = props.getValue()
        return days !== undefined ? `${days} days` : ``
      },
      meta: {
        group: `Patient Information`,
        defaultVisible: slug === HYGIENE_OVERDUE_TAB,
        sortKey: `hygieneDueDate`,
      },
    },
    {
      id: `daysUntilDue`,
      accessorFn: (row) => {
        if (!row.hygieneDueDate) return undefined
        const dueDate = moment.utc(row.hygieneDueDate).format(`YYYY-MM-DD`)
        const daysUntilDue = moment(dueDate).diff(moment(moment().format(`YYYY-MM-DD`)), `days`)
        if (daysUntilDue < 0) return undefined
        return daysUntilDue
      },
      header: `Days Until Due`,
      minSize: 120,
      cell: (props) => {
        const days = props.getValue()
        if (days === undefined) return ``
        return days === 0 ? `Today` : `${days} days`
      },
      meta: {
        group: `Patient Information`,
        defaultVisible: slug === HYGIENE_DUE_SOON_TAB,
        sortKey: `hygieneDueDate`,
      },
    },
    {
      id: `isPerio`,
      accessorFn: (props) => (props.isPerio ? `Yes` : ``),
      header: `Is Perio`,
      meta: {
        group: `Patient Information`,
        defaultVisible: false,
        sortKey: `isPerio`,
      },
    },
    {
      id: `patientReferral`,
      accessorKey: `patientReferral`,
      header: `Patient Referral`,
      meta: {
        group: `Patient Information`,
        defaultVisible: false,
        sortKey: `patientReferral.keyword`,
      },
    },
    {
      id: `becameLeadDate`,
      accessorKey: `becameLeadDate`,
      header: `Became a Lead Date`,
      minSize: 120,
      cell: (props) => DateComponent({ value: props.row.original.becameLeadDate, dateOnly: true }),
      meta: {
        group: `Dates`,
        defaultVisible: !isHygieneTab(slug),
        sortKey: `becameLeadDate`,
      },
    },
    {
      id: `firstAppointmentMadeDate`,
      accessorKey: `firstAppointmentMadeDate`,
      header: `Became a New Patient Scheduled Date`,
      minSize: 120,
      cell: (props) => DateComponent({ value: props.row.original.firstAppointmentMadeDate, dateOnly: true }),
      meta: {
        group: `Dates`,
        defaultVisible: !isHygieneTab(slug),
        sortKey: `firstAppointmentMadeDate`,
      },
    },
    {
      id: `becamePatientDate`,
      accessorKey: `becamePatientDate`,
      header: `Became a Patient Date`,
      minSize: 120,
      cell: (props) => DateComponent({ value: props.row.original.becamePatientDate, dateOnly: true }),
      meta: {
        group: `Dates`,
        defaultVisible: !isHygieneTab(slug),
        sortKey: `becamePatientDate`,
      },
    },
    {
      id: `firstAppointmentDate`,
      accessorKey: `firstAppointmentDate`,
      header: `First Appointment Date`,
      minSize: 120,
      cell: (props) => <DateComponent value={props.row.original.firstAppointmentDate} dateOnly />,
      meta: {
        group: `Dates`,
        defaultVisible: false,
        sortKey: `firstAppointmentDate`,
      },
    },
    {
      id: `lastAppointmentDate`,
      accessorKey: `lastAppointmentDate`,
      header: `Last Appointment Date`,
      minSize: 120,
      cell: (props) => <DateComponent value={props.row.original.lastAppointmentDate} dateOnly />,
      meta: {
        group: `Dates`,
        defaultVisible: false,
        sortKey: `lastAppointmentDate`,
      },
    },
    {
      id: `nearestAppointmentDate`,
      accessorKey: `nearestAppointmentDate`,
      header: `Nearest Appointment Date`,
      minSize: 120,
      cell: (props) => <DateComponent value={props.row.original.nearestAppointmentDate} dateOnly />,
      meta: {
        group: `Dates`,
        defaultVisible: false,
        sortKey: `nearestAppointmentDate`,
      },
    },
    {
      id: `firstTransactionDate`,
      accessorKey: `firstTransactionDate`,
      header: `First Transaction Date`,
      minSize: 120,
      cell: (props) => DateComponent({ value: props.row.original.firstTransactionDate, dateOnly: true }),
      meta: {
        group: `Dates`,
        defaultVisible: false,
        sortKey: `firstTransactionDate`,
      },
    },
    {
      id: `lastTransactionDate`,
      accessorKey: `lastTransactionDate`,
      header: `Last Transaction Date`,
      minSize: 120,
      cell: (props) => DateComponent({ value: props.row.original.lastTransactionDate, dateOnly: true }),
      meta: {
        group: `Dates`,
        defaultVisible: false,
        sortKey: `lastTransactionDate`,
      },
    },
    {
      id: `firstActivityDate`,
      accessorKey: `firstActivityDate`,
      header: `First Activity Date`,
      minSize: 120,
      cell: (props) => <DateComponent value={props.row.original.firstActivityDate} dateOnly />,
      meta: {
        group: `Dates`,
        defaultVisible: false,
        sortKey: `firstActivityDate`,
      },
    },
    {
      id: `lastActivityDate`,
      accessorKey: `lastActivityDate`,
      header: `Last Activity Date`,
      minSize: 120,
      cell: (props) => <DateComponent value={props.row.original.lastActivityDate} dateOnly />,
      meta: {
        group: `Dates`,
        defaultVisible: false,
        pinned: false,
        sortKey: `lastActivityDate`,
      },
    },
    {
      id: `becameInactiveDate`,
      header: `Became Inactive Date`,
      minSize: 120,
      cell: (props) => DateComponent({ value: props.row.original.becameInactiveDate, dateOnly: true }),
      meta: {
        group: `Dates`,
        defaultVisible: false,
        sortKey: `becameInactiveDate`,
      },
    },
    ...(isNeedsActionDateEnabled
      ? [
          {
            id: `needsActionDate`,
            header: `Needs Action Date`,
            minSize: 120,
            cell: (props) => DateComponent({ value: props.row.original.needsActionDate, dateOnly: true }),
            meta: {
              group: `Dates`,
              defaultVisible: false,
              sortKey: `needsActionDate`,
            },
          },
        ]
      : []),
    {
      id: `addressLine1`,
      accessorKey: `addressLine1`,
      header: `Address 1`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        sortKey: `addressLine1.keyword`,
        missing: `_last`,
      },
    },
    {
      id: `addressLine2`,
      accessorKey: `addressLine2`,
      header: `Address 2`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        sortKey: `addressLine2.keyword`,
        missing: `_last`,
      },
    },
    {
      id: `city`,
      accessorKey: `city`,
      header: `City`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        sortKey: `city.keyword`,
        missing: `_last`,
      },
    },
    {
      id: `state`,
      accessorKey: `state`,
      header: `State`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        sortKey: `state.keyword`,
        missing: `_last`,
      },
    },
    {
      id: `zip`,
      accessorKey: `zip`,
      header: `Zip`,
      meta: {
        group: `Contact Information`,
        defaultVisible: false,
        sortKey: `zip.keyword`,
        missing: `_last`,
      },
    },
    ...columnsWithPermissions,
  ]
}

const HYGIENE_RECALL_COLUMNS = [`name`, `tags`, `lastCommDate`, `taskStatus`, `pipelines`, `hygieneDueDate`]

export function hygieneRecallColumns(columns: ColumnDef<ContactJson, any>[]): ColumnDef<ContactJson, any>[] {
  const shown = HYGIENE_RECALL_COLUMNS.flatMap((id) => columns.filter((column) => column.id === id))
  const hidden = columns.filter((column) => !shown.includes(column))
  return [...shown, ...hidden].map((column) => ({
    ...column,
    meta: { ...column.meta, defaultVisible: shown.includes(column) },
  }))
}

export const filterableColumns = ({
  addMarketingFilters,
  isOrgView,
  preAppliedFilters,
  isNeedsActionDateEnabled,
  type,
  isSuper,
  slug,
  isSMC,
  isWokspaceUser: isWorkspaceUser,
  staffV2Enabled,
  checkPermission,
  hygieneEnabled,
  actionCenterEnabled,
  copy,
}: FilterableColumnProps): FilterableColumn[] => {
  // Permission-based filter visibility
  let showMarketingFilters = false
  let showUtmFilters = false
  if (staffV2Enabled) {
    showMarketingFilters = isSuper || checkPermission(`org:marketing:advanced-filters`)
    showUtmFilters = !isSMC && (isSuper || checkPermission(`org:contacts:see-utm`))
  } else {
    // Legacy: addMarketingFilters controls both
    showMarketingFilters = addMarketingFilters
    showUtmFilters = addMarketingFilters
  }

  const commonDateFilterProps = {
    type: FilterValueType.rangeDate,
    defaultValue: [moment().startOf(`month`).format(`YYYY-MM-DD`), moment().endOf(`month`).format(`YYYY-MM-DD`)],
  }

  let location: FilterableColumn = {
    attribute: `locationId`,
    displayName: `Location`,
    optionsSource: `/api/locations`,
    contextualFiltering: { workspaceId: `workspaceId` },
    uniqOperator: `in`,
    type: FilterValueType.id,
    group: `General`,
    standalone: true,
  }

  let tags: FilterableColumn = {
    attribute: `tags`,
    displayName: `Tags`,
    group: `General`,
    fetcher: (params) => tagFilterOptionsFetcher(`Contact`, params),
    type: FilterValueType.stringArray,
  }

  let disqualifiedReason: FilterableColumn = {
    attribute: `closedLostReason`,
    displayName: `Closed Lost Reason`,
    group: `Marketing`,
    type: FilterValueType.multiple,
    options: [
      `Insurance: State / HMO`,
      `Insurance: Out of Network`,
      `Schedule Availability`,
      `Pricing Too High`,
      `Unable to Contact`,
      `Service Not Offered`,
      `Office Too Far`,
      `Scheduled with Competitor`,
      `Other`,
    ].map((option) => ({
      id: option,
      name: option,
      value: option,
    })),
  }

  let organizationStaticFilters = isOrgView
    ? [
        {
          attribute: `workspaceId`,
          displayName: `Workspace`,
          fetcher: (params) => gqlFetchList('workspaces', { search: params.query }, 'id name'),
          type: FilterValueType.id,
          group: `General`,
        } as FilterableColumn,
      ]
    : []

  let workspaceStaticFilters = [tags, location]
  let filters = compact([
    ...organizationStaticFilters,
    ...workspaceStaticFilters,
    {
      attribute: `name`,
      displayName: `Name`,
      type: FilterValueType.string,
      group: `General`,
    },
    {
      attribute: `birthDate`,
      displayName: `Age`,
      type: FilterValueType.age,
      group: `General`,
    },
    ...(isSuper
      ? [
          {
            attribute: `neededAction`,
            displayName: `Needed Action`,
            type: FilterValueType.boolean,
            group: `General`,
            options: [
              {
                id: `yes`,
                name: `Yes`,
                value: `true`,
              },
              {
                id: `no`,
                name: `No`,
                value: `(NOT _exists_:neededAction)`,
              },
            ],
          },
        ]
      : []),
    {
      attribute: `leadOwner.name`,
      displayName: `Contact Owner`,
      fetcher: (params) => staffMembersFetcher(params),
      type: FilterValueType.id,
      defaultValue: undefined,
      queryByName: true,
      group: `Lifecycle / Lead Management`,
    },
    {
      attribute: `leadStatus.keyword`,
      displayName: `Lead Status`,
      type: FilterValueType.multiple,
      group: `Lifecycle / Lead Management`,
      options: [`Needs Action`, `In Progress`, `Scheduled`, `Closed Won`, `Closed Lost`].map((option) => ({
        id: option,
        name: option,
        value: option,
      })),
    },
    {
      attribute: `lifecycleStage.keyword`,
      displayName: `Lifecycle Stage`,
      type: FilterValueType.multiple,
      group: `Lifecycle / Lead Management`,
      options: lifecycleStages.map((option) => ({
        id: option as string,
        name: option as string,
        value: option as string,
      })),
    },
    {
      attribute: `isQualified`,
      displayName: `Qualification Status`,
      type: FilterValueType.multiple,
      group: `Lifecycle / Lead Management`,
      options: [
        { id: `Qualified`, value: `true`, name: `Qualified` },
        { id: `Not Qualified`, value: `false`, name: `Not Qualified` },
        { id: `Empty`, value: `(NOT _exists_:isQualified)`, name: `Empty` },
      ],
    },
    {
      attribute: `activityTypes`,
      displayName: `Has Activity`,
      type: FilterValueType.multiple,
      group: `Lifecycle / Lead Management`,
      options: comActivityTypes?.map((option) => ({
        id: option,
        name: option,
        value: option,
      })),
    },
    {
      attribute: `cellPhone`,
      displayName: `Cell Phone`,
      type: FilterValueType.string,
      group: `Contact Information`,
    },
    {
      attribute: `email`,
      displayName: `Email`,
      type: FilterValueType.string,
      group: `Contact Information`,
    },
    {
      attribute: `homePhone`,
      displayName: `Home Phone`,
      type: FilterValueType.string,
      group: `Contact Information`,
    },
    {
      attribute: `otherPhone`,
      displayName: `Other Phone`,
      type: FilterValueType.string,
      group: `Contact Information`,
    },
    {
      attribute: `preferredCommunicationMethod`,
      displayName: `Preferred Communication Method`,
      type: FilterValueType.string,
      group: `Contact Information`,
    },
    {
      attribute: `preferredContact`,
      displayName: `Preferred Contact`,
      type: FilterValueType.string,
      group: `Contact Information`,
    },
    {
      attribute: `preferredName`,
      displayName: `Preferred Name`,
      type: FilterValueType.string,
      group: `Contact Information`,
    },
    {
      attribute: `workphone`,
      displayName: `Work Phone`,
      type: FilterValueType.string,
      group: `Contact Information`,
    },
    {
      attribute: `becameLeadDate`,
      displayName: `Became a Lead Date`,
      type: FilterValueType.rangeDate,
      dateOperator: DateRangeOperator.last_30_days,
      group: `Dates`,
    },
    isNeedsActionDateEnabled && {
      attribute: `needsActionDate`,
      displayName: `Needs Action Date`,
      type: FilterValueType.rangeDate,
      dateOperator: DateRangeOperator.last_30_days,
      group: `Dates`,
    },
    {
      attribute: `firstAppointmentMadeDate`,
      displayName: `Became a New Patient Scheduled Date`,
      group: `Dates`,
      ...commonDateFilterProps,
    },
    {
      attribute: `becamePatientDate`,
      displayName: `Became a Patient Date`,
      group: `Dates`,
      ...commonDateFilterProps,
    },
    {
      attribute: `becameInactiveDate`,
      displayName: `Became Inactive Date`,
      group: `Dates`,
      ...commonDateFilterProps,
    },
    {
      attribute: `firstActivityDate`,
      displayName: `First Activity Date`,
      group: `Dates`,
      ...commonDateFilterProps,
    },
    {
      attribute: `firstAppointmentDate`,
      displayName: `First Appointment Date`,
      group: `Dates`,
      ...commonDateFilterProps,
    },
    {
      attribute: `firstTransactionDate`,
      displayName: `First Transaction Date`,
      group: `Dates`,
      ...commonDateFilterProps,
    },
    {
      attribute: `lastActivityDate`,
      displayName: `Last Activity Date`,
      group: `Dates`,
      ...commonDateFilterProps,
    },
    {
      attribute: `lastActivityDate`,
      displayName: `Last Appointment Date`,
      group: `Dates`,
      ...commonDateFilterProps,
    },
    {
      attribute: `lastTransactionDate`,
      displayName: `Last Transaction Date`,
      group: `Dates`,
      ...commonDateFilterProps,
    },
    {
      attribute: `nearestAppointmentDate`,
      displayName: `Nearest Appointment Date`,
      group: `Dates`,
      ...commonDateFilterProps,
      includeFuture: true,
    },
    (!isSMC || (isSMC && !isWorkspaceUser)) && {
      attribute: `trackableCampaignId`,
      displayName: `Ad Campaign`,
      group: `Marketing`,
      type: FilterValueType.id,
      defaultValue: [`*`],
      nonEmpty: true,
      optionsSource: `/api/campaigns`,
      customComboOption: ({ selected }) => (
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <DollarCircle height={16} width={16} className="mr-1 text-on-color" style={{ fill: MONEY_ICON_FILL }} />
            <span>Ad Campaign</span>
          </div>
          <Check className={`h-5 w-5 ${selected ? `ml-4 block` : `hidden`}`} />
        </div>
      ),
      additionalParams: { type },
      optionsMapper: (res: Jsonify<SearchResponse<AdCampaign>>) => {
        if (res.total > 0) {
          return uniqBy(
            res.hits
              ?.sort((a, b) => {
                if (a.type && b.type) return a.type?.localeCompare(b?.type)
                return -1
              })
              ?.map(
                (entry) =>
                  ({
                    id: entry.id,
                    value: entry.id,
                    name: entry.name,
                    meta: {
                      customComboOption: ({ selected }) => (
                        <CampaignComboOption
                          campaign={entry}
                          workspaceId={entry.workspaceId}
                          selected={selected}
                          type={entry.type!}
                        />
                      ),
                    },
                  }) as OptionType,
              ),
            `value`,
          )
        }
        return []
      },
    },
    ...(showMarketingFilters
      ? [
          {
            attribute: `platform`,
            displayName: `Ad Platform`,
            type: FilterValueType.boolean,
            group: `Marketing`,
            customComboOption: ({ selected }) => (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DollarCircle
                    height={16}
                    width={16}
                    className="mr-1 text-on-color"
                    style={{ fill: MONEY_ICON_FILL }}
                  />
                  <span>Ad Platform</span>
                </div>
                <Check className={`h-5 w-5 ${selected ? `ml-4 block` : `hidden`}`} />
              </div>
            ),
            options: [
              {
                id: `facebook`,
                name: `Facebook Ads`,
                value: `facebook`,
                meta: { icon: `/public/meta_icon.png` },
              },
              {
                id: `google`,
                name: `Google Ads`,
                value: `google`,
                meta: { icon: `/public/google_ads.png` },
              },
            ],
          },
        ]
      : []),
    ...(showUtmFilters
      ? [
          {
            attribute: `utmCampaign`,
            displayName: `Campaign`,
            group: `Marketing`,
            type: FilterValueType.stringArray,
            optionsSource: `/api/activities/utmCampaign/values?inQueueCallTerm=true&defaultActivitiesTerm=true`,
          },
        ]
      : []),
    {
      attribute: `channel.keyword`,
      displayName: `Channel`,
      type: FilterValueType.asyncSelect,
      nonEmpty: true,
      group: `Marketing`,
      optionsSource: `/api/channels`,
      optionsMapper: (res) => {
        if (res.total > 0) {
          return res.hits.map(
            (entry) =>
              ({
                id: entry,
                value: entry,
                name: entry,
              }) as OptionType,
          )
        }
        return []
      },
    },
    ...(isOrgView ? [] : [disqualifiedReason]),
    ...(showUtmFilters
      ? [
          {
            attribute: `utmContent`,
            displayName: `Content`,
            group: `Marketing`,
            type: FilterValueType.string,
          },
        ]
      : []),
    {
      attribute: `pipelines.id`,
      displayName: copy.Noun,
      type: FilterValueType.id,
      group: `General`,
      fetcher: pipelinesFilterFetcher({ includeSystemPipelines: !!actionCenterEnabled }),
    },
    {
      attribute: `pipelines.key`,
      displayName: copy.listStage,
      type: FilterValueType.id,
      group: `General`,
      fetcher: pipelineStagesFilterFetcher({ includeSystemPipelines: !!actionCenterEnabled }),
      contextualFiltering: { 'pipelines.id': `pipelineId` },
      groupOptions: true,
      optionsMapper: (res) =>
        (res.hits ?? []).map((h: { id: string; name: string; group?: string }) => ({
          id: h.id,
          value: h.id,
          name: h.name,
          meta: { group: h.group },
        })),
    },
    {
      attribute: `firstActivityType`,
      displayName: `First Activity Type`,
      type: FilterValueType.multiple,
      group: `Marketing`,
      options: comActivityTypes.map((type) => ({ id: type, value: type, name: type })),
    },
    {
      attribute: `firstContactReason`,
      displayName: `First Contacted Reason`,
      group: `Marketing`,
      type: FilterValueType.string,
    },
    {
      attribute: `smsMarketingConsent`,
      displayName: `SMS Consent`,
      group: `Marketing`,
      type: FilterValueType.multiple,
      options: [
        { id: `true`, value: `true`, name: `Opted in` },
        { id: `false`, value: `false`, name: `Opted out` },
        { id: `Empty`, value: `(NOT _exists_:smsMarketingConsent)`, name: `Empty` },
      ],
    },
    ...(showUtmFilters
      ? [
          {
            attribute: `utmMedium`,
            displayName: `Medium`,
            group: `Marketing`,
            type: FilterValueType.string,
            // type: FilterValueType.stringArray,
            // optionsSource: `/api/activities/utmMedium/values?inQueueCallTerm=true&defaultActivitiesTerm=true`,
          },
          {
            attribute: `reportedAttribution`,
            displayName: `Reported Attribution`,
            group: `Marketing`,
            type: FilterValueType.string,
          },
          {
            attribute: `utmSource`,
            displayName: `Source`,
            group: `Marketing`,
            type: FilterValueType.string,
            // type: FilterValueType.stringArray,
            // optionsSource: `/api/activities/utmSource/values?inQueueCallTerm=true&defaultActivitiesTerm=true`,
          },
        ]
      : []),
    {
      attribute: `collectedLtv`,
      displayName: `Collected`,
      group: `Financial`,
      type: FilterValueType.number,
    },
    {
      attribute: `productionLtv`,
      displayName: `Net Production`,
      group: `Financial`,
      type: FilterValueType.number,
    },
    {
      attribute: `scheduledProduction`,
      displayName: `Scheduled Production`,
      group: `Financial`,
      type: FilterValueType.number,
    },
    {
      attribute: `fvv`,
      displayName: `First Visit Value`,
      group: `Financial`,
      type: FilterValueType.number,
    },
    {
      attribute: `patientCreatedDate`,
      displayName: `Patient Created Date`,
      group: `Patient Info`,
      ...commonDateFilterProps,
    },
    {
      attribute: `patientIds`,
      displayName: `Patient Linked`,
      group: `Patient Info`,
      type: FilterValueType.attributeExists,
      options: [
        { id: `Linked`, value: `true`, name: `Linked` },
        { id: `Not Linked`, value: `false`, name: `Not Linked` },
      ],
    },
    {
      attribute: `patientName`,
      displayName: `Patient Name`,
      group: `Patient Info`,
      type: FilterValueType.string,
    },
    {
      attribute: `patientReferral`,
      displayName: `Patient Referral`,
      group: `Patient Info`,
      type: FilterValueType.string,
    },
    {
      attribute: `patientStatus`,
      displayName: `Patient Status`,
      defaultValue: [`Active`],
      type: FilterValueType.multiple,
      group: `Patient Info`,
      options: [
        { id: `Active`, value: `Active`, name: `Active` },
        { id: `Inactive`, value: `Inactive`, name: `Inactive` },
        { id: `Archived`, value: `Archived`, name: `Archived` },
        { id: `NonPatient`, value: `NonPatient`, name: `Non-Patient` },
        { id: `blank`, value: `(NOT _exists_:patientStatus)`, name: `Blank` },
      ],
    },
    {
      attribute: `appointmentStatus.keyword`,
      displayName: `Appointment Status`,
      type: FilterValueType.multiple,
      group: `Patient Info`,
      options: [`Unscheduled`, `Scheduled`, `Completed`, `Broken`].map((option) => ({
        id: option,
        name: option,
        value: option,
      })),
    },
    ...(hygieneEnabled
      ? [
          {
            attribute: `lastHygieneDate`,
            displayName: `Last Hygiene Date`,
            group: `Patient Info`,
            ...commonDateFilterProps,
          },
          {
            attribute: `hygieneDueDate`,
            displayName: `Hygiene Due Date`,
            group: `Patient Info`,
            ...commonDateFilterProps,
            includeInThePast: true,
            includeFuture: true,
            includeAll: false,
          },
          {
            attribute: `isPerio`,
            displayName: `Is Perio`,
            group: `Patient Info`,
            type: FilterValueType.multiple,
            options: [
              { id: `true`, value: `true`, name: `Yes` },
              { id: `false`, value: `false`, name: `No` },
            ],
          },
          {
            attribute: `daysPastDue`,
            displayName: `Days Past Due`,
            group: `Patient Info`,
            type: FilterValueType.number,
            defaultOperator: `greater_equal_than` as const,
          },
        ]
      : []),
  ]) as FilterableColumn[]
  const result = filters?.map((filter) => ({
    ...filter,
    ...addDefaultFilterProperties(filter.attribute, preAppliedFilters),
  }))
  return result
}
